/**
 * ModelFormModal Component
 * 
 * Modal for creating/editing models within a platform provider.
 */

import { AppModal } from '@/components/ui/app-modal';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { formResolver } from '@/lib/form-resolver';
import { z } from 'zod';
import { X, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useI18n } from '@/i18n';
import {
  useCreateProviderModel,
  useUpdateProviderModel,
} from '../hooks/usePlatformProviders';
import type { PlatformModel, ModelCategory } from '../types';

// =============================================================================
// Form Schema
// =============================================================================

const modelSchema = z.object({
  model_id: z.string().min(1, 'Model ID is required'),
  display_name: z.string().min(1, 'Display name is required'),
  category: z.enum(['main', 'router', 'tools', 'embedding']),
  context_window: z.number().min(1).default(4096),
  max_output_tokens: z.number().min(1).default(4096),
  supports_vision: z.boolean(),
  supports_tools: z.boolean(),
  supports_chat: z.boolean(),
  supports_embeddings: z.boolean(),
  supports_audio: z.boolean(),
  supports_image_gen: z.boolean(),
  default_temperature: z.number().min(0).max(2).default(0.7),
  default_top_p: z.number().min(0).max(1).default(1.0),
  default_max_tokens: z.number().min(1).default(4096),
  is_active: z.boolean(),
  is_default: z.boolean(),
});

type ModelFormData = z.infer<typeof modelSchema>;

// =============================================================================
// Component
// =============================================================================

interface ModelFormModalProps {
  providerId: string;
  model?: PlatformModel | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ModelFormModal({
  providerId,
  model,
  onClose,
  onSuccess,
}: ModelFormModalProps) {
  const { t } = useI18n();
  const isEditing = !!model;

  const createMutation = useCreateProviderModel();
  const updateMutation = useUpdateProviderModel();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ModelFormData>({
    resolver: formResolver(modelSchema),
    defaultValues: {
      model_id: model?.model_id || '',
      display_name: model?.display_name || '',
      category: model?.category || 'main',
      context_window: model?.context_window || 128000,
      max_output_tokens: model?.max_output_tokens || 4096,
      supports_vision: model?.supports_vision ?? false,
      supports_tools: model?.supports_tools ?? true,
      supports_chat: model?.supports_chat ?? true,
      supports_embeddings: model?.supports_embeddings ?? false,
      supports_audio: model?.supports_audio ?? false,
      supports_image_gen: model?.supports_image_gen ?? false,
      default_temperature: model?.default_temperature ?? 0.7,
      default_top_p: model?.default_top_p ?? 1.0,
      default_max_tokens: model?.default_max_tokens ?? 4096,
      is_active: model?.is_active ?? true,
      is_default: model?.is_default ?? false,
    },
  });

  useEffect(() => {
    if (model) {
      reset({
        model_id: model.model_id,
        display_name: model.display_name,
        category: model.category,
        context_window: model.context_window,
        max_output_tokens: model.max_output_tokens,
        supports_vision: model.supports_vision,
        supports_tools: model.supports_tools,
        supports_chat: model.supports_chat,
        supports_embeddings: model.supports_embeddings,
        supports_audio: model.supports_audio,
        supports_image_gen: model.supports_image_gen,
        default_temperature: model.default_temperature,
        default_top_p: model.default_top_p,
        default_max_tokens: model.default_max_tokens,
        is_active: model.is_active,
        is_default: model.is_default,
      });
    }
  }, [model, reset]);

  const onSubmit = async (data: ModelFormData) => {
    try {
      if (isEditing && model) {
        await updateMutation.mutateAsync({
          providerId,
          modelId: model.id,
          data,
        });
      } else {
        await createMutation.mutateAsync({
          providerId,
          data,
        });
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to save model:', error);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending || isSubmitting;

  const MODEL_CATEGORIES: { value: ModelCategory; label: string; description: string }[] = [
    { value: 'main', label: t('admin.models.categoryMain'), description: t('admin.models.categoryMainDesc') },
    { value: 'router', label: t('admin.models.categoryRouter'), description: t('admin.models.categoryRouterDesc') },
    { value: 'tools', label: t('admin.models.categoryTools'), description: t('admin.models.categoryToolsDesc') },
    { value: 'embedding', label: t('admin.models.categoryEmbedding'), description: t('admin.models.categoryEmbeddingDesc') },
  ];

  return (
    <AppModal
      onClose={onClose}
      ariaLabel={isEditing ? t('admin.models.editTitle') : t('admin.models.addTitle')}
      testId="model-form-modal"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {isEditing ? t('admin.models.editTitle') : t('admin.models.addTitle')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
          {/* Model ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('admin.models.modelId')}
            </label>
            <Input
              {...register('model_id')}
              placeholder={t('admin.models.modelIdPlaceholder')}
              disabled={isEditing}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('admin.models.modelIdHelp')}
            </p>
            {errors.model_id && (
              <p className="text-sm text-red-500 mt-1">{errors.model_id.message}</p>
            )}
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('admin.models.displayName')}
            </label>
            <Input
              {...register('display_name')}
              placeholder={t('admin.models.displayNamePlaceholder')}
            />
            {errors.display_name && (
              <p className="text-sm text-red-500 mt-1">{errors.display_name.message}</p>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('admin.models.category')}
            </label>
            <select
              {...register('category')}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
            >
              {MODEL_CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label} - {cat.description}
                </option>
              ))}
            </select>
          </div>

          {/* Token Limits */}
          <div className="border-t dark:border-gray-700 pt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.models.tokenLimits')}
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500">{t('admin.models.contextWindow')}</label>
                <Input type="number" {...register('context_window', { valueAsNumber: true })} placeholder="128000" />
              </div>
              <div>
                <label className="text-xs text-gray-500">{t('admin.models.maxOutputTokens')}</label>
                <Input type="number" {...register('max_output_tokens', { valueAsNumber: true })} placeholder="4096" />
              </div>
            </div>
          </div>

          {/* Default Settings */}
          <div className="border-t dark:border-gray-700 pt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.models.defaultSettings')}
            </label>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-gray-500">{t('admin.models.temperature')}</label>
                <Input type="number" step="0.1" {...register('default_temperature', { valueAsNumber: true })} placeholder="0.7" />
              </div>
              <div>
                <label className="text-xs text-gray-500">{t('admin.models.topP')}</label>
                <Input type="number" step="0.1" {...register('default_top_p', { valueAsNumber: true })} placeholder="1.0" />
              </div>
              <div>
                <label className="text-xs text-gray-500">{t('admin.models.maxTokens')}</label>
                <Input type="number" {...register('default_max_tokens', { valueAsNumber: true })} placeholder="4096" />
              </div>
            </div>
          </div>

          {/* Capabilities */}
          <div className="border-t dark:border-gray-700 pt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.models.capabilities')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: 'supports_chat', label: t('admin.models.capChat') },
                { key: 'supports_tools', label: t('admin.models.capToolCalling') },
                { key: 'supports_vision', label: t('admin.models.capVision') },
                { key: 'supports_embeddings', label: t('admin.models.capEmbeddings') },
                { key: 'supports_audio', label: t('admin.models.capAudio') },
                { key: 'supports_image_gen', label: t('admin.models.capImageGen') },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2">
                  <input type="checkbox" {...register(key as keyof ModelFormData)} className="rounded border-gray-300" />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Status */}
          <div className="border-t dark:border-gray-700 pt-4 space-y-2">
            <label className="flex items-center gap-2">
              <input type="checkbox" {...register('is_active')} className="rounded border-gray-300" />
              <span className="text-sm font-medium">{t('admin.models.active')}</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" {...register('is_default')} className="rounded border-gray-300" />
              <span className="text-sm font-medium">{t('admin.models.defaultForCategory')}</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t dark:border-gray-700">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('admin.models.cancel')}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEditing ? t('admin.models.saveChanges') : t('admin.models.addModel')}
            </Button>
          </div>
        </form>
      </div>
    </AppModal>
  );
}
